import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Fuse from 'fuse.js';
import {
  ChevronRight,
  ArrowLeft,
  Search,
  X,
  Hand,
  Rocket,
  ChartPie,
  ChartCandlestick,
  Bitcoin,
  Banknote,
  ReceiptText,
  Group,
  Building2,
  FileSearchCorner,
  FileText,
  FileSpreadsheet,
  HatGlasses,
  Database,
  Newspaper,
  Leaf,
  Award,
  Share2,
  MessageCircle,
  Activity,
  ChartNoAxesGantt,
  Calendar,
} from 'lucide-react';
import { getDocsTree } from '@/lib/docs';
import { useDocsScroll } from '@/contexts/DocsScrollContext';
import logo from '@/assets/logo.svg';

const ICONS = {
  '00-introduction': (<Hand size={14} className="mr-2"/>),
  '01-getting-started': (<Rocket size={14} className="mr-2"/>),
  'ETFs': (<ChartPie size={14} className="mr-2"/>),
  'stocks': (<ChartCandlestick size={14} className="mr-2"/>),
  'crypto': (<Bitcoin size={14} className="mr-2"/>),
  'forex': (<Banknote size={14} className="mr-2"/>),
  'futures': (<ReceiptText size={14} className="mr-2"/>),
  'indices': (<Group size={14} className="mr-2"/>),

  'ESG': (<Leaf size={14} className="mr-2"/>),
  'sentiment': (<MessageCircle size={14} className="mr-2"/>),
  'supply-chain': (<Share2 size={14} className="mr-2"/>),
  'web-traffic': (<Activity size={14} className="mr-2"/>),

  'corp.-intelligence': (<Building2 size={14} className="mr-2"/>),
  'earnings': (<FileText size={14} className="mr-2"/>),
  'filings': (<FileSearchCorner size={14} className="mr-2"/>),
  'financials': (<FileSpreadsheet size={14} className="mr-2"/>),
  'insiders': (<HatGlasses size={14} className="mr-2"/>),
  'series': (<ChartNoAxesGantt size={14} className="mr-2"/>),

  'calendar': (<Calendar size={14} className="mr-2"/>),
  'credit-ratings': (<Award size={14} className="mr-2"/>),
  'economic-datasets': (<Database size={14} className="mr-2"/>),
  'news': (<Newspaper size={14} className="mr-2"/>),

  'C-SDK': (<svg width="14px" height="14px" className="mr-2" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fillRule="nonzero" clipRule="nonzero" d="M7.00017 5.5C5.61936 5.5 4.5 6.61936 4.5 8.00017C4.5 9.38097 5.61936 10.5003 7.00017 10.5003C7.71226 10.5003 8.3538 10.2036 8.81009 9.725C9.09591 9.4252 9.57065 9.41386 9.87045 9.69968C10.1703 9.9855 10.1816 10.4602 9.89577 10.76C9.16817 11.5232 8.13926 12.0003 7.00017 12.0003C4.79094 12.0003 3 10.2094 3 8.00017C3 5.79094 4.79094 4 7.00017 4C8.13445 4 9.15952 4.47309 9.88658 5.23069C10.1734 5.52954 10.1636 6.00432 9.86477 6.29112C9.56591 6.57793 9.09114 6.56817 8.80433 6.26931C8.3484 5.79424 7.70928 5.5 7.00017 5.5Z" fill="#4A5464"></path> <path fillRule="nonzero" clipRule="nonzero" d="M14 6C14.2761 6 14.5 6.22386 14.5 6.5V7.5H15.5C15.7761 7.5 16 7.72386 16 8C16 8.27614 15.7761 8.5 15.5 8.5H14.5V9.5C14.5 9.77614 14.2761 10 14 10C13.7239 10 13.5 9.77614 13.5 9.5V8.5H12.5C12.2239 8.5 12 8.27614 12 8C12 7.72386 12.2239 7.5 12.5 7.5H13.5V6.5C13.5 6.22386 13.7239 6 14 6Z" fill="#4A5464"></path> <path fillRule="nonzero" clipRule="nonzero" d="M11.5 6C11.7761 6 12 6.22386 12 6.5V7.5H13C13.2761 7.5 13.5 7.72386 13.5 8C13.5 8.27614 13.2761 8.5 13 8.5H12V9.5C12 9.77614 11.7761 10 11.5 10C11.2239 10 11 9.77614 11 9.5V8.5H10C9.72386 8.5 9.5 8.27614 9.5 8C9.5 7.72386 9.72386 7.5 10 7.5H11V6.5C11 6.22386 11.2239 6 11.5 6Z" fill="#4A5464"></path> <path fillRule="nonzero" clipRule="nonzero" d="M6.58638 0.102166C6.8199 -0.0340553 7.10867 -0.0340553 7.34219 0.102166L12.0565 2.85217C12.4143 3.06088 12.5351 3.52011 12.3264 3.8779C12.1177 4.23569 11.6585 4.35654 11.3007 4.14783L6.96429 1.61828L1.5 4.80578V11.1942L6.96429 14.3817L11.3007 11.8522C11.6585 11.6435 12.1177 11.7643 12.3264 12.1221C12.5351 12.4799 12.4143 12.9391 12.0565 13.1478L7.34219 15.8978C7.10867 16.0341 6.8199 16.0341 6.58638 15.8978L0.372097 12.2728C0.141683 12.1384 0 11.8918 0 11.625V4.375C0 4.10825 0.141683 3.86157 0.372097 3.72717L6.58638 0.102166Z" fill="#4A5464"></path> </g></svg>),
  'javascript-SDK': (<svg width="14px" height="14px" className="mr-2"  viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fillRule="nonzero" clipRule="nonzero" d="M0 1.75C0 0.783501 0.783502 0 1.75 0H14.25C15.2165 0 16 0.783502 16 1.75V3.75C16 4.16421 15.6642 4.5 15.25 4.5C14.8358 4.5 14.5 4.16421 14.5 3.75V1.75C14.5 1.61193 14.3881 1.5 14.25 1.5H1.75C1.61193 1.5 1.5 1.61193 1.5 1.75V14.25C1.5 14.3881 1.61193 14.5 1.75 14.5H15.25C15.6642 14.5 16 14.8358 16 15.25C16 15.6642 15.6642 16 15.25 16H1.75C0.783501 16 0 15.2165 0 14.25V1.75ZM8.25 5.75C8.66421 5.75 9 6.08579 9 6.5V10.5C9 11.5048 8.72399 12.2584 8.15514 12.7324C7.61223 13.1848 6.95384 13.25 6.5 13.25C6.08579 13.25 5.75 12.9142 5.75 12.5C5.75 12.0858 6.08579 11.75 6.5 11.75C6.84617 11.75 7.06277 11.6902 7.19486 11.5801C7.301 11.4916 7.5 11.2452 7.5 10.5V6.5C7.5 6.08579 7.83578 5.75 8.25 5.75ZM11.2757 6.58011C11.6944 6.08164 12.3507 5.75 13.25 5.75C14.0849 5.75 14.7148 6.03567 15.1394 6.48481C15.4239 6.78583 15.4105 7.26052 15.1095 7.54505C14.8085 7.82958 14.3338 7.81621 14.0493 7.51519C13.9394 7.39898 13.7204 7.25 13.25 7.25C12.7493 7.25 12.5306 7.41836 12.4243 7.54489C12.2934 7.70065 12.25 7.896 12.25 8C12.25 8.104 12.2934 8.29935 12.4243 8.45511C12.5306 8.58164 12.7493 8.75 13.25 8.75C13.3257 8.75 13.3988 8.76121 13.4676 8.78207C14.1307 8.87646 14.6319 9.17251 14.9743 9.58011C15.3684 10.0493 15.5 10.604 15.5 11C15.5 11.396 15.3684 11.9507 14.9743 12.4199C14.5556 12.9184 13.8993 13.25 13 13.25C12.1651 13.25 11.5352 12.9643 11.1106 12.5152C10.8261 12.2142 10.8395 11.7395 11.1405 11.4549C11.4415 11.1704 11.9162 11.1838 12.2007 11.4848C12.3106 11.601 12.5296 11.75 13 11.75C13.5007 11.75 13.7194 11.5816 13.8257 11.4551C13.9566 11.2993 14 11.104 14 11C14 10.896 13.9566 10.7007 13.8257 10.5449C13.7194 10.4184 13.5007 10.25 13 10.25C12.9243 10.25 12.8512 10.2388 12.7824 10.2179C12.1193 10.1235 11.6181 9.82749 11.2757 9.41989C10.8816 8.95065 10.75 8.396 10.75 8C10.75 7.604 10.8816 7.04935 11.2757 6.58011Z" fill="#4A5464"></path> </g></svg>),
  'python-SDK': (<svg width="14px" height="14px" className="mr-2" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M5.79 1.574h3.866c.14 0 .252.11.252.246v5.186a.25.25 0 01-.252.246H6.344c-.975 0-1.766.77-1.766 1.72v1.162a.25.25 0 01-.253.243H1.867a.25.25 0 01-.253-.246V6.177a.25.25 0 01.252-.246H7.98c.418 0 .757-.33.757-.737a.747.747 0 00-.757-.738H5.537V1.82a.25.25 0 01.253-.246zm5.632 2.592V1.82c0-.95-.79-1.72-1.766-1.72H5.79c-.976 0-1.767.77-1.767 1.72v2.636H1.867C.89 4.456.1 5.226.1 6.176v3.955c0 .95.79 1.72 1.766 1.72h2.46c.085 0 .17-.006.252-.017v2.346c0 .95.79 1.72 1.766 1.72h3.866c.976 0 1.767-.77 1.767-1.72v-2.636h2.156c.976 0 1.767-.77 1.767-1.72V5.868c0-.95-.79-1.72-1.767-1.72h-2.458c-.086 0-.17.005-.253.017zm-5.33 5.974V8.994a.25.25 0 01.252-.246h3.312c.976 0 1.766-.77 1.766-1.72V5.866a.25.25 0 01.253-.243h2.458c.14 0 .253.11.253.246v3.954a.25.25 0 01-.252.246H8.02a.747.747 0 00-.757.737c0 .408.339.738.757.738h2.442v2.636a.25.25 0 01-.253.246H6.344a.25.25 0 01-.252-.246v-4.04z" fill="#4A5464"></path> </g></svg>),

  '01-core': (<span className="mr-2 px-1 py-0.5 text-[9px] font-mono font-semibold tracking-wide rounded bg-gray-300 text-white leading-none shrink-0">.PY</span>),
  'models': (<span className="mr-2 px-1 py-0.5 text-[9px] font-mono font-semibold tracking-wide rounded bg-gray-300 text-white leading-none shrink-0">.PY</span>),
  'technical-analysis': (<span className="mr-2 px-1 py-0.5 text-[9px] font-mono font-semibold tracking-wide rounded bg-gray-300 text-white leading-none shrink-0">.PY</span>),
  'utilities-and-helpers': (<span className="mr-2 px-1 py-0.5 text-[9px] font-mono font-semibold tracking-wide rounded bg-gray-300 text-white leading-none shrink-0">.PY</span>),
  'visualizations': (<span className="mr-2 px-1 py-0.5 text-[9px] font-mono font-semibold tracking-wide rounded bg-gray-300 text-white leading-none shrink-0">.PY</span>),
}

interface DocFile {
  name: string;
  path: string;
  children?: DocFile[];
}

interface DocsSidebarProps {
  openSearchModal: () => void;
  onLinkClick?: () => void;
}

const DocsSidebar: React.FC<DocsSidebarProps> = ({ openSearchModal, onLinkClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const { currentDocPath, setCurrentDocPath } = useDocsScroll();
  const navRef = useRef<HTMLElement>(null);
  const isInitialSidebarScroll = useRef(true);

  const [fuse, setFuse] = useState<DocFile[]>([]);
  const [docsTree, setDocsTree] = useState<DocFile[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [prevDocPath, setPrevDocPath] = useState<string>('');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const takeToHome = () => {
    onLinkClick?.();
    navigate(token ? '/dashboard' : '/');
  };

  function flatten(items: NavItem[]) {
  return items.flatMap(function walk(item): any[] {
    return [
      {
        name: item.name.replaceAll('-', ' ').replace(/[0-9]/g, '').trim(),
        pathName: item.name.replaceAll('-', ' ').replaceAll('/', ' ').replace(/[0-9]/g, '').trim(),
        path: item.path,
      },
      ...(item.children?.flatMap(walk) ?? []),
    ];
  });
}

  useEffect(() => {
    getDocsTree().then((hi) => {
      setDocsTree(hi)
      if (hi.length > 0 && window.innerWidth >= 1024) {
        setCollapsed(
          Object.fromEntries(hi.filter(n => n.children).map(n => [n.path, false]))
        );
      }

      const flattened = flatten(hi);
      const fuse = new Fuse(flattened, {
        keys: [
          { name: 'name',     weight: 0.5 },  // primary label - most intuitive to search
          { name: 'pathName', weight: 0.3 },  // same content but kept for explicit matches
          { name: 'path',     weight: 0.2 },  // path segments catch breadcrumb-style queries
        ],
        threshold: 0.3,          // tighter than 0.4 - reduces noise in results
        distance: 100,           // how far into the string a match can be (pairs with ignoreLocation: false)
        ignoreLocation: true,    // still useful since path values are long strings
        minMatchCharLength: 0,   // avoid single-char false positives
        useExtendedSearch: true, // enables prefix (^etf), exact ("holdings"), negation (!crypto)
        includeScore: true,      // lets you sort/filter by score downstream if needed
        shouldSort: true,        // best matches first
        findAllMatches: false,   // stop at best match per item, keeps results clean
      });
      setFuse(fuse)
    });
  }, []);

  useEffect(() => {
    if (isInitialSidebarScroll.current) {
      isInitialSidebarScroll.current = false;
      return;
    }

    if (!navRef.current || !currentDocPath) return;

    // Wait for DOM + collapse animations to settle
    requestAnimationFrame(() => {
      const activeLink = navRef.current?.querySelector(
        '[data-active="true"]'
      ) as HTMLElement | null;

      const nav = navRef.current;

      if (!activeLink) {
        nav.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        const navRect = nav.getBoundingClientRect();
        const linkRect = activeLink.getBoundingClientRect();
        const offset =
          linkRect.top -
          navRect.top -
          nav.clientHeight / 2 +
          linkRect.height / 2;

        nav.scrollTo({
          top: nav.scrollTop + offset,
          behavior: 'smooth'
        });
      }
    });
  }, [currentDocPath, searchQuery]);

  // Detect when current doc changes and trigger animation
  useEffect(() => {
    if (currentDocPath && currentDocPath !== prevDocPath && prevDocPath !== '') {
      setIsTransitioning(true);
      const timer = setTimeout(() => setIsTransitioning(false), 600);
      return () => clearTimeout(timer);
    }
    if (currentDocPath) {
      setPrevDocPath(currentDocPath);
    }
  }, [currentDocPath, prevDocPath]);

  // Find all ancestor paths for a given target path in the tree
  const findAncestors = (tree: DocFile[], targetPath: string, ancestors: string[] = []): string[] | null => {
    for (const node of tree) {
      if (node.path === targetPath) return ancestors;
      if (node.children) {
        const result = findAncestors(node.children, targetPath, [...ancestors, node.path]);
        if (result !== null) return result;
      }
    }
    return null;
  };

  // Expand all ancestors when active path changes
  useEffect(() => {
    const activePath = currentDocPath || location.pathname.replace('/docs/', '');
    if (!activePath || !docsTree.length) return;

    const ancestors = findAncestors(docsTree, activePath);
    if (ancestors && ancestors.length > 0) {
      setCollapsed((prev) => {
        const next = { ...prev };
        ancestors.forEach((p) => { next[p] = false; });
        return next;
      });
    }
  }, [currentDocPath, docsTree]);

  const toggleCollapse = (path: string) => {
  setCollapsed((prev) => {
    const current = prev[path] !== undefined ? prev[path] : true;
    return { ...prev, [path]: !current };
  });
};


  // Fuzzy search function
  const fuzzyMatch = (results, path: string): boolean => {
    return results.find((r) => {
      return r.item.path === path
    })
  };

  // Filter docs tree based on search query
  const filterTree = (tree: DocFile[], query: string): DocFile[] => {
    if (!query) return tree;

    const res = fuse.search(query);
    return tree
      .map((node) => {
        const matchesCurrent = fuzzyMatch(res, node.path);

        if (node.children) {
          const filteredChildren = filterTree(node.children, query);
          const matchesChildren = filteredChildren.length > 0;

          if (matchesCurrent || matchesChildren) {
            return {
              ...node,
              children: filteredChildren
            };
          }
          return null;
        }

        return matchesCurrent ? node : null;
      })
      .filter(Boolean) as DocFile[];
  };

  const filteredTree = searchQuery ? filterTree(docsTree, searchQuery) : docsTree;

  const renderTree = (tree: DocFile[], depth: number = 0) => {
    if (tree.length === 0) {
      return (
        <div className="px-4 py-2 text-sm text-gray-500 text-center">
          No matching documents found
        </div>
      );
    }

    return tree.map((node,i) => {
      const isCollapsed = searchQuery ? false : (collapsed[node.path] !== undefined ? collapsed[node.path] : true);

      const displayName = node.name.replaceAll('-', ' ').replace(/\d+/g, '');
      const isActive = currentDocPath ? currentDocPath === node.path : location.pathname === `/docs/${node.path}`;
      const showTransition = isActive && isTransitioning;

      return (
        <div key={`${node.path}-${i}`}>
          <div className="flex items-center justify-between relative" >
            {node.children ? (
              <button
                data-active={isActive && !node.children ? "true" : "false"}
                data-path={node.path}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  toggleCollapse(node.path);
                }}
                style={{ paddingLeft: `${(depth * 12 + 12) / 2}px`, marginLeft: `${(depth * 12 + 12) / 2}px` }}
                className={`
                  flex items-center w-full px-2 py-2 text-sm font-medium capitalize rounded-md cursor-pointer transition-all hover:bg-gray-50
                  ${isActive ? "text-blue-400" : `text-gray-600`}
                `}
              >
                {node.name in ICONS ? ICONS[node.name] : null}
                <span className="flex items-center gap-2 relative">
                  {isActive && showTransition && (
                    <>
                      <span className={`w-2 h-2 rounded-full bg-blue-300  z-[99] animate-ping absolute z-0 ${ICONS[node.name] ? 'left-[-20px]' : 'left-[-25px]'}`} />
                    </>
                  )}
                  {displayName}
                </span>
                <ChevronRight
                  size={16}
                  className={`transform mx-1 transition-transform ${isCollapsed ? "" : "rotate-90"}`}
                />
              </button>
            ) : (
              <Link
                to={`/docs/${node.path}`}
                data-active={isActive ? "true" : "false"}
                data-path={node.path}
                onClick={(e) => {
                  if (location.pathname === `/docs/${node.path}`) {
                    e.preventDefault();
                    onLinkClick?.();
                    const container = document.getElementById('docs-scroll-container');
                    if (container) {
                      const targetEl = container.querySelector(`[data-doc-path="${node.path}"]`) as HTMLElement;
                      if (targetEl) {
                        setCurrentDocPath(node.path);
                        container.scrollTo({
                          top: targetEl.offsetTop,
                          behavior: 'smooth'
                        });
                      }
                    }
                  } else {
                    onLinkClick?.();
                  }
                }}
                style={{ paddingLeft: `${(depth * 12 + 12) / 2}px`, marginLeft: `${(depth * 12 + 12) / 2}px` }}
                className={`
                  flex items-center w-full px-2 py-2 text-sm font-medium capitalize rounded-md no-underline transition-all hover:bg-gray-50
                  ${isActive ? "text-blue-400" : `text-gray-600`}
                `}
              >
                {node.name in ICONS ? ICONS[node.name] : (
                  <span className="mr-2 px-1 py-0.5 text-[9px] font-mono font-semibold tracking-wide rounded bg-gray-300 text-white leading-none shrink-0 ">
                    GET
                  </span>
                )}
                <span className="flex items-center gap-2 relative">
                  {isActive && showTransition && (
                    <>
                      <span className={`w-2 h-2 rounded-full bg-blue-300  z-[99] animate-ping absolute z-0 ${ICONS[node.name] ? 'left-[-20px]' : 'left-[-25px]'}`} />
                    </>
                  )}
                  {displayName}
                </span>
              </Link>
            )}
          </div>
          {!isCollapsed && node.children && (
            <div>{renderTree(node.children, depth + 1)}</div>
          )}
        </div>
      );
    });
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div className="flex flex-col h-full w-64 bg-white border-r border-gray-200 overflow-hidden dashboard-sidebar">
      <div className="p-6 border-b border-gray-200">

        <div className="flex items-center gap-2 cursor-pointer group" onClick={takeToHome}>
          <div className="w-6 h-6 rounded-md flex items-center justify-center relative transition-all duration-200 group-hover:scale-102 group-hover:translate-x-[-2px] group-hover:opacity-80">
            <img
              src={logo}
              alt="Logo"
              className="pointer-none select-none absolute inset-0 w-full h-full transition-opacity duration-200 opacity-100 group-hover:opacity-0"
              draggable="false"
            />
            <ArrowLeft
              className="w-4 h-4 text-black absolute inset-0 m-auto opacity-0 transition-opacity duration-200 group-hover:opacity-100"
              strokeWidth={2.5}
            />
          </div>
          <span className="font-semibold select-none text-black transition-all duration-200 group-hover:opacity-80">AXION</span>
        </div>

      </div>

      {/* Search Input */}
      <div className="p-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Find a doc..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 text-sm text-gray-700 bg-gray-100 rounded-md focus:outline-none focus:bg-white focus:ring-1 focus:ring-gray-900"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <nav ref={navRef} className="relative flex-1 px-4 pb-4 space-y-1 overflow-y-auto min-h-0">
        {renderTree(filteredTree)}
      </nav>

      <div className="p-3 border-t border-gray-100 space-y-1 justify-self-end">
        <button
          className="flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors text-gray-600 hover:bg-gray-50 hover:text-gray-900 w-full"
          onClick={() => { onLinkClick?.(); openSearchModal(); }}
        >
          <Search className="w-4 h-4" />
          <span>Search</span>
          <span className="ml-auto text-xs text-gray-400">⌘K</span>
        </button>
      </div>
    </div>
  );
};

export default DocsSidebar;
